import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import type { TrafficRow } from './rows';
import { STORM_FLOOR_PPS } from './rows';
import { MirrorTooltip, type MirrorSeries } from './MirrorTooltip';
import { ShowValues } from './ShowValues';
import { CHART_MARGIN, Y_AXIS_WIDTH } from './chart';
import { formatCount, formatPercent } from './format';

const FACET_HEIGHT = 84;

/**
 * Errors, discards and the storm ratio, as small multiples on one x-axis.
 *
 * Each facet keeps its **own** y-scale on purpose. On a congested port discards
 * run a thousand times the error rate, so a shared y flattens errors onto the
 * baseline — and errors are the more diagnostic of the two: corruption means a
 * bad optic, a bad cable or a duplex mismatch, while discards mean congestion.
 * Two measures of different scale is what small multiples are for; a second
 * y-axis is the answer that lies about it.
 *
 * Every facet mirrors on the same in-above / out-below convention as the
 * throughput chart, so the reader never re-learns the geometry.
 */
export function FaultFacets({
  rows,
  start,
  end,
  syncId,
  hasErrors,
  hasDiscards,
  hasPacketMix,
  boundOverrides,
}: {
  rows: TrafficRow[];
  start: number;
  end: number;
  syncId: string;
  hasErrors: boolean;
  hasDiscards: boolean;
  hasPacketMix: boolean;
  /**
   * Force each facet's magnitude, so a link panel's two ends share one scale
   * and can be compared by height rather than only by label.
   */
  boundOverrides?: { errors?: number; discards?: number };
}) {
  const spansDays = end - start > 24 * 3600 * 1000;

  return (
    <div>
      <h3 className="text-xs font-semibold mb-3">Faults</h3>

      <Facet
        title="Errors"
        rows={rows}
        start={start}
        end={end}
        syncId={syncId}
        spansDays={spansDays}
        showAxis={false}
        reported={hasErrors}
        boundOverride={boundOverrides?.errors}
        emptyCaption="No errors counted at this port in this window."
        series={[
          { dataKey: 'errorsIn', label: 'inbound errors', color: 'var(--series-1)', format: formatCount },
          { dataKey: 'errorsOut', label: 'outbound errors', color: 'var(--series-2)', format: formatCount },
        ]}
      />

      <Facet
        title="Discards"
        rows={rows}
        start={start}
        end={end}
        syncId={syncId}
        spansDays={spansDays}
        showAxis={!hasPacketMix}
        reported={hasDiscards}
        boundOverride={boundOverrides?.discards}
        emptyCaption="No discards counted at this port in this window."
        series={[
          { dataKey: 'discardsIn', label: 'inbound discards', color: 'var(--series-1)', format: formatCount },
          { dataKey: 'discardsOut', label: 'outbound discards', color: 'var(--series-2)', format: formatCount },
        ]}
      />

      {hasPacketMix ? (
        <StormRatio rows={rows} start={start} end={end} syncId={syncId} spansDays={spansDays} />
      ) : null}

      <ShowValues
        label="Show fault values"
        columns={[
          { key: 'ts', label: 'Time' },
          { key: 'errIn', label: 'Err in' },
          { key: 'errOut', label: 'Err out' },
          { key: 'discIn', label: 'Disc in' },
          { key: 'discOut', label: 'Disc out' },
          { key: 'share', label: 'Non-unicast' },
        ]}
        rows={rows.map((row) => ({
          ts: format(new Date(row.ts), spansDays ? 'MMM d, HH:mm' : 'HH:mm:ss'),
          errIn: cell(row.errorsIn, formatCount),
          errOut: cell(row.errorsOut, formatCount),
          discIn: cell(row.discardsIn, formatCount),
          discOut: cell(row.discardsOut, formatCount),
          share: cell(row.nonUnicastShare, formatPercent),
        }))}
      />
    </div>
  );
}

function cell(value: number | null, formatter: (v: number) => string): string {
  return value === null ? '—' : formatter(Math.abs(value));
}

/**
 * A facet with nothing in it draws a flat baseline and says so, rather than
 * blanking. At 3am, zero errors is what the reader came to confirm.
 */
function Facet({
  title,
  rows,
  start,
  end,
  syncId,
  spansDays,
  showAxis,
  reported,
  boundOverride,
  emptyCaption,
  series,
}: {
  title: string;
  rows: TrafficRow[];
  start: number;
  end: number;
  syncId: string;
  spansDays: boolean;
  showAxis: boolean;
  reported: boolean;
  boundOverride?: number;
  emptyCaption: string;
  series: MirrorSeries[];
}) {
  const ownBound = useMemo(() => {
    const magnitudes = rows.flatMap((row) =>
      series.flatMap((spec) => {
        const value = row[spec.dataKey];
        return typeof value === 'number' ? [Math.abs(value)] : [];
      }),
    );
    const peak = magnitudes.length > 0 ? Math.max(...magnitudes) : 0;
    return peak > 0 ? peak * 1.2 : 1;
  }, [rows, series]);
  const bound = boundOverride !== undefined && boundOverride > 0 ? boundOverride : ownBound;

  const quiet = ownBound === 1;

  return (
    <div className="mb-1">
      <div className="flex items-baseline gap-2">
        <h4 className="text-[11px] font-medium text-muted-foreground">{title}</h4>
        {!reported ? (
          <span className="text-[11px] text-muted-foreground">not reported by this device</span>
        ) : quiet ? (
          <span className="text-[11px] text-muted-foreground">{emptyCaption}</span>
        ) : null}
      </div>
      <ResponsiveContainer width="100%" height={showAxis ? FACET_HEIGHT + 22 : FACET_HEIGHT}>
        <LineChart data={rows} syncId={syncId} syncMethod="value" margin={CHART_MARGIN}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={[start, end]}
            hide={!showAxis}
            tickFormatter={(ts: number) => format(new Date(ts), spansDays ? 'MMM d HH:mm' : 'HH:mm')}
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            minTickGap={48}
          />
          <YAxis
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 10 }}
            width={Y_AXIS_WIDTH}
            domain={[-bound, bound]}
            // Three, not `tickCount`: a facet is 84px tall and the middle one
            // has to be the zero line. See `mirroredTicks`.
            ticks={[-bound, 0, bound]}
            // Render all three. Recharts culls ticks it thinks will collide,
            // and at 84px tall it drops the bottom one — which on a mirror is
            // the outbound rail, the half the reader is least able to infer.
            interval={0}
            tickFormatter={(v: number) => (v === 0 ? '0' : formatCount(Math.abs(v)))}
          />
          <ReferenceLine y={0} stroke="var(--border)" strokeWidth={1} />
          <Tooltip
            cursor={{ stroke: 'var(--fg-subtle)', strokeWidth: 1 }}
            content={<MirrorTooltip series={series} spansDays={spansDays} />}
          />
          {series.map((spec) => (
            <Line
              key={spec.dataKey}
              type="monotone"
              dataKey={spec.dataKey}
              stroke={spec.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const STORM_SERIES: MirrorSeries[] = [
  {
    dataKey: 'nonUnicastShare',
    label: 'non-unicast share of inbound',
    color: 'var(--series-3)',
    format: formatPercent,
  },
];

/**
 * The share of inbound packets that is broadcast or multicast.
 *
 * This replaces the stacked packet-mix chart the obvious design reaches for: on
 * a healthy port the mix is 98/1/1, so stacked segments are sub-pixel hairlines
 * whose labels drop out, and the block only ever earns its place for one
 * finding — a broadcast storm. One ratio answers that yes-or-no question with
 * no new hues and no third unit on the strip.
 *
 * Buckets carrying fewer than {@link STORM_FLOOR_PPS} inbound packets a second
 * are drawn faint, because a quiet access port with three idle workstations is
 * nearly 100% non-unicast by packet count — there is no unicast to dilute the
 * ARP. Two lines rather than one variable-opacity line: recharts cannot vary a
 * stroke along its length, and splitting the series is what makes the low
 * confidence visible instead of asserted in a caption nobody reads.
 */
function StormRatio({
  rows,
  start,
  end,
  syncId,
  spansDays,
}: {
  rows: TrafficRow[];
  start: number;
  end: number;
  syncId: string;
  spansDays: boolean;
}) {
  const split = useMemo(
    () =>
      rows.map((row) => {
        const busy = (row.inboundPps ?? 0) >= STORM_FLOOR_PPS;
        return {
          ...row,
          nonUnicastShare: busy ? row.nonUnicastShare : null,
          nonUnicastShareQuiet: busy ? null : row.nonUnicastShare,
        };
      }),
    [rows],
  );

  return (
    <div className="mb-1">
      <div className="flex items-baseline gap-2">
        <h4 className="text-[11px] font-medium text-muted-foreground">Non-unicast share of inbound</h4>
        <span className="text-[11px] text-muted-foreground">
          faint below {String(STORM_FLOOR_PPS)} packets/s, where the ratio means little
        </span>
      </div>
      <ResponsiveContainer width="100%" height={FACET_HEIGHT + 22}>
        <LineChart data={split} syncId={syncId} syncMethod="value" margin={CHART_MARGIN}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={[start, end]}
            tickFormatter={(ts: number) => format(new Date(ts), spansDays ? 'MMM d HH:mm' : 'HH:mm')}
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            minTickGap={48}
          />
          <YAxis
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 10 }}
            width={Y_AXIS_WIDTH}
            domain={[0, 100]}
            tickCount={3}
            tickFormatter={(v: number) => `${String(v)}%`}
          />
          <Tooltip
            cursor={{ stroke: 'var(--fg-subtle)', strokeWidth: 1 }}
            content={<MirrorTooltip series={STORM_SERIES} spansDays={spansDays} />}
          />
          <Line
            type="monotone"
            dataKey="nonUnicastShareQuiet"
            stroke="var(--series-3)"
            strokeOpacity={0.4}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="nonUnicastShare"
            stroke="var(--series-3)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
